import { readonly, ref } from "vue";

/**
 * LSP-17976: Scribe answers every request from a user whose membership is archived for the token's
 * organization with 403 `{ error: "Forbidden", message: "Your account has been archived for this
 * organization" }` (scribe-api `middleware/validate-user-organization.ts`; the message is pinned by
 * that repo's `validate-user-organization.unit.test.ts`). There is no machine code, and the plain
 * "You do not have access to this organization" 403 shares the status, so the message is the only
 * discriminator.
 *
 * Contact used to swallow that 403 in every Spark surface and render "No Sparks yet" / "No reports
 * yet", so an archived user believed the product was broken.
 *
 * The error funnels only raise an unattributed signal here. A response does not say which
 * organization's token made the request, and reading the session when it lands would blame the wrong
 * organization if the user switched in between. useScribeAccess turns the signal into a verdict with a
 * probe that captures its organization before the request goes out.
 */
const ARCHIVED_MESSAGE_PATTERN = /archived/i;

const archivedSignal = ref(0);

/** Bumped every time a Scribe call is refused for an archived membership. */
export const scribeAccessArchivedSignal = readonly(archivedSignal);

export function isScribeAccessArchivedError(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const { status, message } = error as { status?: unknown; message?: unknown };
	return status === 403 && typeof message === "string" && ARCHIVED_MESSAGE_PATTERN.test(message);
}

/** Called from both Scribe error funnels with every error they build. */
export function noteScribeAccessError(error: unknown): void {
	if (!isScribeAccessArchivedError(error)) return;
	archivedSignal.value++;
}
